import { useDeferredValue, useId, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

import type { FitMessage, TimeDisplayMode } from '@fit-editor/fit-core'

import { useFieldCell } from '@/components/data/field-cell-renderer'
import { FieldKindTag } from '@/components/data/message-table'
import type { FitDataModel } from '@/components/data/use-fit-data-model'
import { Button } from '@/components/ui/button'
import { baseTypeName, describeMessageFields, fieldMatches, hasData } from '@/lib/fit-data'
import type { FieldRow, MessageGroup } from '@/lib/fit-data'
import { formatDecodedValue, formatRawValue } from '@/lib/fit-data-format'

/** Instances shown per page inside one expanded message group. */
export const MESSAGES_PAGE_SIZE = 25
/** Groups with more instances than this start collapsed (e.g. records). */
const AUTO_EXPAND_MAX = 10

export interface AllMessagesPanelProps {
  model: FitDataModel
  source: 'current' | 'original'
  timeMode: TimeDisplayMode
}

interface DescribedInstance {
  index: number
  fields: FieldRow[]
  /** Formatted value per field, aligned with `fields` (computed once, reused for search + render). */
  formatted: string[]
}

interface DescribedGroup {
  group: MessageGroup
  instances: DescribedInstance[]
}

/**
 * Every message in the file, grouped by type: each instance's fields as
 * name / value / units / raw value. Undocumented fields (numeric keys the
 * Profile doesn't define, preserved by the raw-fit layer) and developer
 * fields are tagged; preserved unknown message types are listed with
 * their global message number and raw values.
 */
export function AllMessagesPanel({ model, source, timeMode }: AllMessagesPanelProps) {
  const [query, setQuery] = useState('')
  const [onlyWithData, setOnlyWithData] = useState(true)
  const deferredQuery = useDeferredValue(query.trim().toLowerCase())
  const searchId = useId()

  // Describing + formatting every field of every instance is the
  // expensive part (records dominate); done once per activity / time mode
  // / "only with data" setting -- never per keystroke or per render.
  const described = useMemo(
    () => describeAll(model, timeMode, !onlyWithData),
    [model, timeMode, onlyWithData],
  )

  const filtered = useMemo(() => {
    const out: DescribedGroup[] = []
    for (const entry of described) {
      if (deferredQuery.length === 0 || entry.group.name.includes(deferredQuery)) {
        out.push(entry)
        continue
      }
      const instances: DescribedInstance[] = []
      for (const inst of entry.instances) {
        const keep: number[] = []
        inst.fields.forEach((f, i) => {
          if (fieldMatches(f, deferredQuery, inst.formatted[i] ?? '')) keep.push(i)
        })
        if (keep.length > 0) {
          instances.push({
            index: inst.index,
            fields: keep.map((i) => inst.fields[i] as FieldRow),
            formatted: keep.map((i) => inst.formatted[i] as string),
          })
        }
      }
      if (instances.length > 0) out.push({ group: entry.group, instances })
    }
    return out
  }, [described, deferredQuery])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={searchId} className="sr-only">
          Search messages and fields
        </label>
        <input
          id={searchId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search message, field, or value…"
          className="border-input bg-background focus-visible:ring-ring h-8 w-full max-w-sm rounded-md border px-2 text-sm outline-none focus-visible:ring-2"
        />
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={onlyWithData}
            onChange={(e) => setOnlyWithData(e.target.checked)}
            className="accent-primary"
          />
          Only fields with data
        </label>
      </div>

      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {deferredQuery.length > 0
            ? `No messages or fields match "${query.trim()}".`
            : 'This file has no messages.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2" aria-label="Message types">
          {filtered.map((entry) => (
            <MessageGroupSection
              // Re-mount (resetting expansion/page) when a search starts or ends.
              key={`${entry.group.id}|${deferredQuery.length > 0}`}
              entry={entry}
              searching={deferredQuery.length > 0}
              source={source}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function describeAll(
  model: FitDataModel,
  timeMode: TimeDisplayMode,
  includeEmpty: boolean,
): DescribedGroup[] {
  const { activity, profile, byKey, groups, devFields, unknownBaseTypes } = model
  const unknownByNum = new Map<number, typeof activity.preservedUnknownMessages>()
  for (const m of activity.preservedUnknownMessages) {
    const list = unknownByNum.get(m.globalMessageNumber) ?? []
    list.push(m)
    unknownByNum.set(m.globalMessageNumber, list)
  }

  return groups.map((group) => {
    let instances: DescribedInstance[]
    if (group.unknownType) {
      instances = (unknownByNum.get(group.mesgNum ?? -1) ?? []).map((m, index) => {
        const fields: FieldRow[] = m.fieldDefs.map((def, i) => {
          const value = m.values[i]
          return {
            id: String(def.fieldDefinitionNumber),
            name: `field_${def.fieldDefinitionNumber}`,
            kind: 'undocumented',
            num: def.fieldDefinitionNumber,
            value,
            units: '',
            raw: undefined,
            baseType: baseTypeName(def.baseType),
            meta: undefined,
            hasData: hasData(value),
          }
        })
        return finish(index, fields, timeMode, includeEmpty)
      })
    } else {
      const key = group.messagesKey as string
      const messageMeta = profile?.byMessagesKey.get(key)
      instances = (byKey.get(key) ?? []).map((message: FitMessage, index) =>
        finish(
          index,
          describeMessageFields(message, {
            messageMeta,
            devFields,
            unknownBaseTypes: unknownBaseTypes.get(`${key}#${index}`),
            includeEmpty,
          }),
          timeMode,
          includeEmpty,
        ),
      )
    }
    return { group, instances }
  })
}

function finish(
  index: number,
  allFields: FieldRow[],
  timeMode: TimeDisplayMode,
  includeEmpty: boolean,
): DescribedInstance {
  const fields = includeEmpty ? allFields : allFields.filter((f) => f.hasData)
  return { index, fields, formatted: fields.map((f) => formatDecodedValue(f.value, timeMode)) }
}

function MessageGroupSection({
  entry,
  searching,
  source,
}: {
  entry: DescribedGroup
  searching: boolean
  source: 'current' | 'original'
}) {
  const { group, instances } = entry
  const [expanded, setExpanded] = useState(
    () => group.count <= AUTO_EXPAND_MAX || (searching && instances.length <= AUTO_EXPAND_MAX),
  )
  const [page, setPage] = useState(0)
  const regionId = useId()
  const pageCount = Math.max(1, Math.ceil(instances.length / MESSAGES_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageStart = safePage * MESSAGES_PAGE_SIZE
  const visible = instances.slice(pageStart, pageStart + MESSAGES_PAGE_SIZE)

  return (
    <li className="border-border bg-card rounded-lg border">
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={regionId}
          onClick={() => setExpanded((v) => !v)}
          className="hover:bg-muted/60 focus-visible:ring-ring flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm outline-none focus-visible:ring-2"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span className="font-mono font-medium">{group.name}</span>
          {group.mesgNum != null && (
            <span className="text-muted-foreground text-xs">#{group.mesgNum}</span>
          )}
          {group.unknownType && <FieldKindTag kind="undocumented" />}
          <span className="text-muted-foreground ml-auto text-xs tabular-nums">
            {searching && instances.length !== group.count
              ? `${instances.length.toLocaleString()} of ${group.count.toLocaleString()} match`
              : `${group.count.toLocaleString()} message${group.count === 1 ? '' : 's'}`}
          </span>
        </button>
      </h3>
      {expanded && (
        <div id={regionId} className="border-border flex flex-col gap-3 border-t p-3">
          {group.unknownType && (
            <p className="text-warning-text text-xs">
              Message type {group.mesgNum} isn&apos;t defined by the FIT Profile. Raw values are
              shown as stored and preserved verbatim on FIT export.
            </p>
          )}
          {visible.map((inst) => (
            <InstanceTable key={inst.index} group={group} instance={inst} source={source} />
          ))}
          {pageCount > 1 && (
            <nav className="flex items-center gap-2 text-xs" aria-label={`${group.name} pages`}>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                Previous
              </Button>
              <span className="tabular-nums" aria-live="polite">
                {(pageStart + 1).toLocaleString()}–
                {Math.min(pageStart + MESSAGES_PAGE_SIZE, instances.length).toLocaleString()} of{' '}
                {instances.length.toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                Next
              </Button>
            </nav>
          )}
        </div>
      )}
    </li>
  )
}

function InstanceTable({
  group,
  instance,
  source,
}: {
  group: MessageGroup
  instance: DescribedInstance
  source: 'current' | 'original'
}) {
  const renderCell = useFieldCell()
  const showRaw = instance.fields.some((f) => f.raw !== undefined)
  return (
    <div className="border-border overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-left text-xs">
        <caption className="bg-muted/40 px-2 py-1 text-left font-medium">
          {group.name} {instance.index + 1}
        </caption>
        <thead>
          <tr>
            <th scope="col" className="w-1/4 px-2 py-1 font-semibold">
              Field
            </th>
            <th scope="col" className="px-2 py-1 font-semibold">
              Value
            </th>
            <th scope="col" className="px-2 py-1 font-semibold">
              Units
            </th>
            {showRaw && (
              <th scope="col" className="px-2 py-1 font-semibold">
                Raw
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {instance.fields.length === 0 ? (
            <tr>
              <td colSpan={showRaw ? 4 : 3} className="text-muted-foreground px-2 py-1">
                No fields with data.
              </td>
            </tr>
          ) : (
            instance.fields.map((field, i) => (
              <tr key={field.id} className="border-border border-t" data-field-kind={field.kind}>
                <th scope="row" className="px-2 py-1 font-mono font-normal whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    {field.name}
                    {field.num != null && field.kind !== 'undocumented' && (
                      <span className="text-muted-foreground">({field.num})</span>
                    )}
                    {field.baseType != null && (
                      <span className="text-muted-foreground">{field.baseType}</span>
                    )}
                    <FieldKindTag kind={field.kind} />
                  </span>
                </th>
                <td className="px-2 py-1 break-all tabular-nums">
                  {renderCell(
                    {
                      source,
                      surface: 'allMessages',
                      messagesKey: group.messagesKey,
                      mesgNum: group.mesgNum,
                      messageIndex: instance.index,
                      fieldId: field.id,
                      kind: field.kind,
                      value: field.value,
                      units: field.units,
                      meta: field.meta,
                    },
                    instance.formatted[i],
                  )}
                </td>
                <td className="text-muted-foreground px-2 py-1">{field.units}</td>
                {showRaw && (
                  <td className="text-muted-foreground px-2 py-1 tabular-nums">
                    {formatRawValue(field.raw)}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
